import React, { Fragment, useEffect, useState } from "react";
import { Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import { getPaginationRange } from "services/utils";

interface GoalContainerProps {
  columns: any[];
  data: any[];
  customPageSize?: number;

  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  divClass?: string;

  pagination?: {
    page: number;
    total_items: number;
    total_pages: number;
    per_page: number;
  };

  onPageChange?: (page: number) => void;
  limit: number;
  setLimit?: React.Dispatch<React.SetStateAction<number>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onRowClick?: (row: any) => void;

  onEditRemark?: (goalId: number, currentRemark: string) => void;
}

const Filter = ({ column }: { column: Column<any, unknown> }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <input
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder="Search..."
      className="form-control search mt-1"
    />
  );
};

const GoalContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  divClass,

  pagination,
  onPageChange,
  setLimit,
  limit,
  setPage,

  onRowClick,
}: GoalContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const rank = rankItem(row.getValue(columnId), value);
    addMeta({ rank });
    return rank.passed;
  };

  const singleLineCell = (content: string) => (
    <div className="d-flex align-items-center justify-content-between">
      <span
        style={{
          maxWidth: "180px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={content}
      >
        {content}
      </span>
    </div>
  );

  const renderNameCell = (cell: any) => {
    const row = cell.row.original;
    const content = row.taskname ?? "";
    return singleLineCell(content);
  };

  const twoLineCell = (content: string) => (
    <div className="d-flex align-items-center">
      <span
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "1.5",
          maxHeight: "3em",
          wordBreak: "break-word",
        }}
        title={content}
      >
        {content}
      </span>
    </div>
  );

  const renderDescriptionCell = (cell: any) => {
    const row = cell.row.original;
    const content = row.description ?? "";
    const goalId = row.id;

    return twoLineCell(content);
  };

  const mergedColumns = columns.map((col: any) =>
    col.accessorKey === "description"
      ? { ...col, cell: renderDescriptionCell }
      : col.accessorKey === "taskname"
      ? { ...col, cell: renderNameCell }
      : col
  );

  const table = useReactTable({
    columns: mergedColumns,
    data,
    filterFns: { fuzzy: fuzzyFilter },

    state: { columnFilters, globalFilter },

    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,

    globalFilterFn: fuzzyFilter,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  useEffect(() => {
    if (customPageSize) setPageSize(customPageSize);
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={trClass}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={"border-none border-0"}
                    style={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={trClass}
                style={{
                  ...(rowIndex % 2 === 0 && { backgroundColor: "#73C3A91F" }),
                  verticalAlign: "middle",
                  whiteSpace: "pre-line",
                }}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell: any) => (
                  <td key={cell.id} style={{ verticalAlign: "middle" }}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {pagination && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <Col sm>
            <div className="text-muted">
              Showing{" "}
              <span className="fw-semibold ms-1">
                {Math.min(
                  pagination.per_page * pagination.page,
                  pagination.total_items
                )}
              </span>{" "}
              of{" "}
              <span className="fw-semibold">{pagination.total_items}</span>{" "}
              Results
            </div>
          </Col>

          <Col sm="auto">
            <ul className="pagination pagination-separated pagination-md mb-0">
              <li
                className={`page-item ${
                  pagination.page <= 1 ? "disabled" : ""
                }`}
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={() => onPageChange?.(pagination.page - 1)}
                >
                  Previous
                </Link>
              </li>

              {getPaginationRange(
                pagination.page,
                pagination.total_pages
              ).map((p, idx) => (
                <li
                  key={idx}
                  className={`page-item ${
                    p === pagination.page ? "active" : ""
                  } ${p === "..." ? "disabled" : ""}`}
                >
                  {p === "..." ? (
                    <span className="page-link">...</span>
                  ) : (
                    <Link
                      to="#"
                      className="page-link"
                      onClick={() => onPageChange?.(p as number)}
                    >
                      {p}
                    </Link>
                  )}
                </li>
              ))}

              <li
                className={`page-item ${
                  pagination.page >= pagination.total_pages ? "disabled" : ""
                }`}
              >
                <Link
                  className="page-link"
                  to="#"
                  onClick={() => onPageChange?.(pagination.page + 1)}
                >
                  Next
                </Link>
              </li>
            </ul>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default GoalContainer;
